import React, { useState, useEffect } from "react";
import api from "../../lib/api";
import { Plus, Eye, EyeOff, Edit2, Trash2, Copy, X, Save, Wand2, RefreshCw } from "lucide-react";
import { useConfirm } from "../../contexts/ConfirmContext";

const CATS = ["Router", "Windows", "E-Mail", "VPN", "NAS", "Server", "Switch", "Sonstiges"];

// ── Secure password generator ──────────────────────────────────────────
const CHARS = {
  upper: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  lower: "abcdefghijklmnopqrstuvwxyz",
  numbers: "0123456789",
  symbols: "!@#$%^&*()_+-=[]{}|;:,.<>?",
};

function generatePassword(length, opts) {
  let pool = "";
  if (opts.upper) pool += CHARS.upper;
  if (opts.lower) pool += CHARS.lower;
  if (opts.numbers) pool += CHARS.numbers;
  if (opts.symbols) pool += CHARS.symbols;
  if (!pool) return "";
  const arr = new Uint32Array(length);
  window.crypto.getRandomValues(arr);
  return Array.from(arr).map(v => pool[v % pool.length]).join("");
}

function PasswordGenerator({ onUse }) {
  const [length, setLength] = useState(16);
  const [opts, setOpts] = useState({ upper: true, lower: true, numbers: true, symbols: true });
  const [generated, setGenerated] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = () => setGenerated(generatePassword(length, opts));

  const copy = async () => {
    if (!generated) return;
    await navigator.clipboard.writeText(generated);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Show initial password on mount
  useEffect(() => { setGenerated(generatePassword(length, opts)); }, []);

  const toggleOpt = (k) => {
    const next = { ...opts, [k]: !opts[k] };
    setOpts(next);
    setGenerated(generatePassword(length, next));
  };

  const changeLen = (v) => {
    setLength(v);
    setGenerated(generatePassword(v, opts));
  };

  return (
    <div className="bg-muted/30 border border-border p-4 mb-4 animate-in" data-testid="password-generator">
      <div className="flex items-center gap-2 mb-3">
        <Wand2 size={14} className="text-primary" />
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Passwort-Generator</span>
      </div>

      {/* Options row */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        {/* Length slider */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-14">Länge: <strong className="text-foreground">{length}</strong></span>
          <input
            data-testid="pw-gen-length"
            type="range" min={8} max={32} step={1}
            value={length} onChange={e => changeLen(Number(e.target.value))}
            className="w-24 accent-primary"
          />
        </div>
        {/* Checkboxes */}
        {[
          { key: "upper", label: "A-Z" },
          { key: "lower", label: "a-z" },
          { key: "numbers", label: "0-9" },
          { key: "symbols", label: "!@#" },
        ].map(o => (
          <label key={o.key} className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={opts[o.key]}
              onChange={() => toggleOpt(o.key)}
              className="w-3.5 h-3.5 accent-primary"
              data-testid={`pw-gen-opt-${o.key}`}
            />
            <span className="font-mono font-semibold">{o.label}</span>
          </label>
        ))}
      </div>

      {/* Generated password display */}
      <div className="flex items-center gap-2">
        <div className="flex-1 px-3 py-2 bg-background border border-input font-mono text-sm tracking-wider select-all min-h-[38px] flex items-center">
          {generated || <span className="text-muted-foreground italic text-xs">Zeichenauswahl treffen...</span>}
        </div>
        <button onClick={generate} title="Neu generieren"
          className="p-2 border border-border bg-background hover:bg-secondary transition-colors" data-testid="pw-gen-regenerate">
          <RefreshCw size={14} />
        </button>
        <button onClick={copy} title={copied ? "Kopiert!" : "Kopieren"}
          className={`p-2 border transition-colors ${copied ? "border-green-500 bg-green-50 text-green-600 dark:bg-green-950/20" : "border-border bg-background hover:bg-secondary"}`}
          data-testid="pw-gen-copy">
          <Copy size={14} />
        </button>
        {onUse && (
          <button onClick={() => onUse(generated)} disabled={!generated}
            className="px-3 py-2 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-40 transition-colors"
            data-testid="pw-gen-use">
            Verwenden
          </button>
        )}
      </div>
    </div>
  );
}

// ── Password row ────────────────────────────────────────────────────────
function PwRow({ entry, customerId, onDelete, onUpdate }) {
  const [revealed, setRevealed] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState({ title: entry.title, username: entry.username || "", password: "", url: entry.url || "", category: entry.category || "", notes: entry.notes || "" });
  const [copying, setCopying] = useState(false);

  const reveal = async () => {
    if (revealed) { setRevealed(null); return; }
    const r = await api.get(`/customers/${customerId}/passwords/${entry._id}/reveal`);
    setRevealed(r.data);
  };

  const copy = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopying(true);
    setTimeout(() => setCopying(false), 1500);
  };

  const save = async (e) => {
    e.preventDefault();
    const r = await api.put(`/customers/${customerId}/passwords/${entry._id}`, form);
    onUpdate(r.data);
    setShowEdit(false);
    setRevealed(null);
  };

  return (
    <>
      <tr data-testid={`password-row-${entry._id}`}>
        <td className="font-medium">{entry.title}</td>
        <td className="text-xs text-muted-foreground">{entry.category || "—"}</td>
        <td className="font-mono text-xs">{entry.username || "—"}</td>
        <td>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs">{revealed ? revealed.password : "••••••••"}</span>
            <button onClick={reveal} className="text-muted-foreground hover:text-primary transition-colors" data-testid={`reveal-password-${entry._id}`}>
              {revealed ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
            {revealed && (
              <button onClick={() => copy(revealed.password)} className={`transition-colors ${copying ? "text-green-500" : "text-muted-foreground hover:text-primary"}`}>
                <Copy size={13} />
              </button>
            )}
          </div>
        </td>
        <td className="text-xs text-muted-foreground max-w-[120px] truncate">{entry.url || "—"}</td>
        <td>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowEdit(!showEdit)} className="text-muted-foreground hover:text-primary transition-colors"><Edit2 size={13} /></button>
            <button onClick={() => onDelete(entry._id)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={13} /></button>
          </div>
        </td>
      </tr>
      {showEdit && (
        <tr>
          <td colSpan={6} className="bg-muted/30 p-4">
            <form onSubmit={save} className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { name: "title", label: "Bezeichnung *", required: true },
                { name: "username", label: "Benutzername" },
                { name: "url", label: "URL" },
                { name: "notes", label: "Notizen" },
              ].map(f => (
                <div key={f.name}>
                  <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">{f.label}</label>
                  <input value={form[f.name]} onChange={e => setForm({ ...form, [f.name]: e.target.value })} required={f.required}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
                </div>
              ))}
              {/* Password field with generator */}
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Neues Passwort</label>
                <div className="flex gap-1">
                  <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder="Leer = unverändert"
                    className="flex-1 px-2 py-1.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
                  <button type="button"
                    onClick={() => { const p = generatePassword(16, { upper: true, lower: true, numbers: true, symbols: true }); setForm({ ...form, password: p }); }}
                    className="px-2 py-1.5 border border-border bg-background hover:bg-secondary text-xs" title="Generieren">
                    <Wand2 size={12} />
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Kategorie</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm bg-background border border-input focus:border-primary focus:outline-none">
                  <option value="">—</option>
                  {CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-span-full flex gap-2 mt-1">
                <button type="submit" className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 flex items-center gap-1"><Save size={12} /> Speichern</button>
                <button type="button" onClick={() => setShowEdit(false)} className="px-3 py-1.5 bg-secondary text-xs font-semibold flex items-center gap-1"><X size={12} /> Abbrechen</button>
              </div>
            </form>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main component ──────────────────────────────────────────────────────
export default function PasswordsTab({ customerId }) {
  const confirm = useConfirm();
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [form, setForm] = useState({ title: "", username: "", password: "", url: "", category: "", notes: "" });

  useEffect(() => {
    api.get(`/customers/${customerId}/passwords`).then(r => setEntries(r.data));
  }, [customerId]);

  const create = async (e) => {
    e.preventDefault();
    const r = await api.post(`/customers/${customerId}/passwords`, form);
    setEntries([...entries, r.data]);
    setForm({ title: "", username: "", password: "", url: "", category: "", notes: "" });
    setShowForm(false);
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Passwort löschen", message: "Dieser Tresor-Eintrag wird unwiderruflich entfernt.", confirmLabel: "Löschen" }))) return;
    await api.delete(`/customers/${customerId}/passwords/${id}`);
    setEntries(entries.filter(e => e._id !== id));
  };

  const update = (updated) => setEntries(entries.map(e => e._id === updated._id ? updated : e));

  return (
    <div className="max-w-5xl animate-in">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Passwort-Tresor</h2>
        <div className="flex gap-2">
          <button
            data-testid="toggle-generator-button"
            onClick={() => setShowGenerator(!showGenerator)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary text-secondary-foreground text-xs font-semibold hover:bg-secondary/80"
          >
            <Wand2 size={13} /> Generator
          </button>
          <button data-testid="add-password-button" onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90">
            <Plus size={13} /> Neuer Eintrag
          </button>
        </div>
      </div>

      {/* Password Generator panel */}
      {showGenerator && (
        <PasswordGenerator onUse={(pw) => {
          setForm({ ...form, password: pw });
          setShowForm(true);
          setShowGenerator(false);
        }} />
      )}

      {/* New entry form */}
      {showForm && (
        <form onSubmit={create} className="bg-card border border-border p-4 mb-4 animate-in" data-testid="create-password-form">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { name: "title", label: "Bezeichnung *", required: true },
              { name: "username", label: "Benutzername" },
              { name: "url", label: "URL" },
              { name: "notes", label: "Notizen" },
            ].map(f => (
              <div key={f.name}>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">{f.label}</label>
                <input data-testid={`password-form-${f.name}`} type="text"
                  value={form[f.name]} onChange={e => setForm({ ...form, [f.name]: e.target.value })}
                  required={f.required}
                  className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
              </div>
            ))}
            {/* Password field with generator */}
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Passwort</label>
              <div className="flex gap-1">
                <input data-testid="password-form-password" type="text" value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  className="flex-1 px-3 py-2 text-sm font-mono bg-background border border-input focus:border-primary focus:outline-none" />
                <button type="button"
                  onClick={() => setForm({ ...form, password: generatePassword(16, { upper: true, lower: true, numbers: true, symbols: true }) })}
                  className="px-3 border border-border bg-background hover:bg-secondary transition-colors" title="Sicheres Passwort generieren"
                  data-testid="password-form-generate">
                  <Wand2 size={14} />
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Kategorie</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none">
                <option value="">—</option>
                {CATS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button data-testid="save-password-button" type="submit" className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90">Speichern</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 bg-secondary text-xs font-semibold">Abbrechen</button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="bg-card border border-border" data-testid="passwords-table">
        {entries.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Keine Passwörter gespeichert</div>
        ) : (
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Bezeichnung</th><th>Kategorie</th><th>Benutzername</th>
                <th>Passwort</th><th>URL</th><th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => <PwRow key={e._id} entry={e} customerId={customerId} onDelete={remove} onUpdate={update} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
